import {Box, IconButton, Typography} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import FilterTextField from '../../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {t} from 'i18next';
import './JobResultDataGrid.scss';
import {ReactComponent as AIIconRelevancy} from '../../../../../assets/images/ai-review-relevancy.svg';
import {ReactComponent as RightChevronIcon} from '../../../../../assets/images/icon-right-outline.svg';
import {ReactComponent as WarningIcon} from '../../../../../assets/images/warning.svg';
import {ReactNode} from 'react';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import ArrTooltip from '../../../../common/arr-tooltip/ArrTooltip';
import {IPreviewDoc} from '../JobResults';
import {IAIReviewRelevancyDocSelection} from '../../../../../api/types/aiReview.type';

const wrap = (content: ReactNode) => {
  const cell = (
    <CellWrapper className="tooltip_content" dataTestId="tooltip_content">
      {content}
    </CellWrapper>
  );

  return (
    <ArrTooltip
      trigger="hover"
      placement="bottom"
      content={content}
      labelId={`tooltip-cell-cont-${content}`}
    >
      {cell}
    </ArrTooltip>
  );
};

const normalizeCellValue = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isYesValue = (value: unknown) => {
  const normalized = normalizeCellValue(value);
  return normalized === 'yes' || normalized === 'relevant';
};

const isExcludedValue = (value: unknown) =>
  normalizeCellValue(value) === 'excluded';

const getYesNoStyleClass = (value: unknown) => {
  const displayValue = value == null ? '' : String(value);
  if (!displayValue) {
    return '';
  }
  if (isExcludedValue(value)) {
    return ' relevancy-excluded-text';
  }
  if (!isYesValue(value)) {
    return ' no_typo_text';
  }
  return '';
};

const renderYesNoStyleCell = (
  params: {id: string | number; value?: unknown},
  testIdPrefix: string
) => {
  const displayValue = params.value == null ? '' : String(params.value);

  return (
    <Box
      tabIndex={0}
      role="gridcell"
      data-testid={`${testIdPrefix}-${params.id}-box`}
    >
      <Typography
        className={`cell-content typo-text body3_size${getYesNoStyleClass(
          params.value
        )}`}
        data-testid={`${testIdPrefix}-${params.id}`}
        tabIndex={0}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

export const relevancyColumns = (
  handleDocumentClicked: (props: IPreviewDoc) => void,
  onOpenRelevancyDocPanel?: (selection: IAIReviewRelevancyDocSelection) => void
): GridColDef[] => [
  {
    field: 'docId',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.docId'),
    sortable: true,
    filterable: false,
    width: 160,
    minWidth: 160,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        data-testid={`relevancy-column-doc-id-header`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.docId')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        className="relevancy-doc-id-cell"
        tabIndex={0}
        aria-label={`${params.value}, ${t('ai.review.jobTypeDataGrid.relevenacyColumns.docId')}`}
        data-testid={`docId-column-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text subhead5_size job-value"
          data-testid={`relevancy-column-docId-${params.id}`}
          aria-hidden="true"
          onClick={() => {
            handleDocumentClicked({
              id: Number(params.row.documentId),
              docId: String(params.row.docId ?? ''),
            });
          }}
        >
          {params.value}
        </Typography>
        {params.row?.isFlaggedForHumanReview && (
          <WarningIcon
            className="relevancy-doc-id-warning-icon"
            data-testid={`relevancy-column-docId-warning-${params.id}`}
            aria-hidden="true"
          />
        )}
      </Box>
    ),
  },
  {
    field: 'relevancy',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.relevancy'),
    sortable: true,
    filterable: true,
    minWidth: 170,
    flex: 1,
    valueGetter: (_value, row) => row?.relevancy ?? row?.relevency ?? '',
    renderCell: (params) =>
      renderYesNoStyleCell(params, 'relevancy-column-relevancy-'),
    renderHeader: () => {
      return (
        <Box
          sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
          tabIndex={0}
          role="columnheader"
          data-testid={`relevancy-column-relevancy-box`}
        >
          <AIIconRelevancy />
          <Typography
            className="typo-text label2_size"
            data-testid={`relevancy-column-relevancy-header`}
          >
            {t('ai.review.jobTypeDataGrid.relevenacyColumns.relevancy')}
          </Typography>
        </Box>
      );
    },
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`relevancy`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'groundTruth',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.groundTruth'),
    sortable: true,
    filterable: true,
    minWidth: 170,
    flex: 1,
    renderCell: (params) =>
      renderYesNoStyleCell(params, 'relevancy-column-groundTruth'),
    renderHeader: () => (
      <Typography
        tabIndex={0}
        role="columnheader"
        className="typo-text label2_size"
        data-testid={`relevancy-column-ground-truth-headr`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.groundTruth')}
      </Typography>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`groundTruth`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'confidence',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.confidence'),
    sortable: true,
    filterable: true,
    minWidth: 140,
    flex: 1,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        role="columnheader"
        className="typo-text label2_size"
        data-testid={`relevancy-column-confidence-header`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.confidence')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        role="gridcell"
        data-testid={`relevancy-column-confidence-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text body3_size"
          data-testid={`relevancy-column-confidence-${params.id}`}
        >
          {params.value ?? ''}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`confidence`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'reason',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.reason'),
    sortable: true,
    filterable: false,
    minWidth: 393.5,
    flex: 6,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        role="columnheader"
        data-testid={`relevancy-column-reason-header`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.reason')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        data-testid={`relevancy-column-excerpt-box-${params.id}`}
      >
        <Typography
          component="div"
          className="cell-content typo-text body3_size"
          data-testid={`relevancy-column-reason-${params.id}`}
        >
          {wrap(params.value)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'openDocument',
    headerName: '',
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    width: 56,
    minWidth: 56,
    maxWidth: 56,
    align: 'center',
    headerAlign: 'center',
    headerClassName: 'last-column',
    cellClassName: 'last-column',
    resizable: false,
    renderHeaderFilter: () => null,
    renderCell: (params) => (
      <IconButton
        className="relevancy-open-document-button"
        data-testid={`relevancy-column-open-document-${params.id}`}
        aria-label={t(
          'ai.review.jobTypeDataGrid.relevenacyColumns.openDocument'
        )}
        size="small"
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          onOpenRelevancyDocPanel?.({
            reviewJobSubmittedId: params.row.reviewJobSubmittedId,
            docId: String(params.row.docId ?? ''),
          });
        }}
      >
        <RightChevronIcon />
      </IconButton>
    ),
  },
];
